import { FeeSplitEditor, IFeeSplitLine, useFeeSplitPayees } from 'components/FeeSplits'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getDrawFeeSplit, queueDrawFeeSplit, saveDrawFeeSplit } from 'services'
import { Modal } from 'stories/components'
import { getPrice2decimal, removeComma, thousandSeperatorWithPrefix } from 'utils'

/** One payout already raised from this row's split, as the server reports it. */
interface IDrawFeePayable {
  id: number
  payeeName: string
  amount: number
  status: string
  referenceNumber: string
}

/** A share as the server resolves it today: the payee it would actually pay, and how much. */
interface IDrawFeePayout {
  payeeName: string
  percent: number
  amount: number
}

const STATUS_STYLE: Record<string, string> = {
  paid: 'text-green-600',
  queued: 'text-amber-600',
  voided: 'text-gray-400 line-through',
}

/**
 * Split a construction draw's inspection fee between the people it is owed to, and raise their
 * shares in the check queue (Smartsheet row 376).
 *
 * The split is set here rather than on the draw entry form for two reasons. The fee has to exist
 * before it can be divided - the server refuses a split on a row with no fee - and most rows a
 * servicer splits were entered earlier, so an entry-form-only editor would not reach them. It also
 * keeps one editor for both entry paths, the loan's own form and the pipeline's Draw modal.
 *
 * Shares are percentages, so they stay right when the fee is edited. What they do not claim stays
 * with the lender, who funded the fee out of the reserve.
 */
export const DrawFeeSplitDialog = ({
  loanNumber,
  row,
  onClose,
  onSaved,
}: {
  loanNumber: number
  /** The Construction Reserve row being split: its id, inspection fee and current shares. */
  row: Record<string, any>
  onClose: () => void
  /** Called after a save or a queue, so the grid can reload the row and its payables. */
  onSaved: () => void
}) => {
  const payees = useFeeSplitPayees(loanNumber)
  const [splits, setSplits] = useState<IFeeSplitLine[]>([])
  const [payouts, setPayouts] = useState<IDrawFeePayout[]>([])
  const [payables, setPayables] = useState<IDrawFeePayable[]>([])
  const [locked, setLocked] = useState(false)
  const [loading, setLoading] = useState(true)

  const fee = Number(removeComma(row?.inspectionFee)) || 0

  const load = async () => {
    setLoading(true)
    const res: any = await getDrawFeeSplit(Number(row.id)).catch(() => null)
    // The row's stored shares come back with the row itself; the server's view of the payouts and
    // what has been raised from them comes from this call.
    setPayouts(Array.isArray(res?.payouts) ? res.payouts : [])
    setPayables(Array.isArray(res?.payables) ? res.payables : [])
    setLocked(!!res?.locked)
    setLoading(false)
  }

  useEffect(() => {
    setSplits(Array.isArray(row?.inspectionFeeSplits) ? row.inspectionFeeSplits : [])
    load()
    // eslint-disable-next-line
  }, [row?.id])

  /**
   * Save the shares. Only the shares are sent: the row's own route merges that one key, so nothing
   * else on the row can be lost on the way through.
   */
  const onSave = async () => {
    setLoading(true)
    const res: any = await saveDrawFeeSplit(Number(row.id), splits).catch(() => null)
    setLoading(false)
    // The server refuses a split that claims more than the whole fee, one with a share nobody is
    // named for, and any change once a share has been paid. Its message says which, and is worth
    // more than a generic failure toast.
    if (!res || res.success === 0) return toast(res?.message || 'That split could not be saved.', { type: 'error' })
    setPayouts(Array.isArray(res?.payouts) ? res.payouts : [])
    setPayables(Array.isArray(res?.payables) ? res.payables : [])
    setLocked(!!res?.locked)
    toast('Inspection fee split saved.', { type: 'success' })
    onSaved()
  }

  /** Raise the shares as payouts, or bring already-queued ones back into line with the row. */
  const onQueue = async () => {
    setLoading(true)
    const res: any = await queueDrawFeeSplit(Number(row.id)).catch(() => null)
    setLoading(false)
    if (!res) return toast('Those payouts could not be raised.', { type: 'error' })
    setPayouts(Array.isArray(res?.payouts) ? res.payouts : [])
    setPayables(Array.isArray(res?.payables) ? res.payables : [])
    setLocked(!!res?.locked)
    if (res?.message) toast(res.message, { type: 'info' })
    else toast('The inspection fee shares are in the check queue.', { type: 'success' })
    onSaved()
  }

  const live = payables.filter((p) => p.status !== 'voided')
  const claimed = payouts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)

  return (
    <Modal
      title="Split Inspection Fee"
      titleOkay="Save Split"
      onOk={onSave}
      onClose={onClose}
      loading={loading}
      isOpen
      lastUpdatedAt={Date.now()}
    >
      <div className="w-[640px] flex flex-col gap-3">
        <div className="text-sm text-gray-600">
          <span className="font-semibold">{thousandSeperatorWithPrefix(fee)}</span> inspection fee on draw{' '}
          <span className="font-semibold">{row?.reference || row?.date || `#${row.id}`}</span>
        </div>

        {fee <= 0 && (
          <p className="text-sm text-red-600">
            This row has no inspection fee, so there is nothing to split. Add the fee to the draw first.
          </p>
        )}

        <FeeSplitEditor
          value={splits}
          onChange={setSplits}
          payees={payees}
          disabled={locked || fee <= 0}
          title="Paid to"
          hint="Each payee below is queued for a check with their share of this inspection fee. Paying those checks posts nothing further to the trust ledger - this draw already records the fee leaving the reserve."
          remainderLabel="Lender keeps:"
          emptyText="Not split — the lender keeps the whole inspection fee, as it did before."
        />

        {payouts.length > 0 && (
          <div className="border border-slate-200 rounded-lg p-3">
            <p className="font-semibold text-sm mb-2">Shares as they would be paid</p>
            {payouts.map((p, i) => (
              <div className="flex justify-between text-sm py-0.5" key={i}>
                <span>
                  {p.payeeName} <span className="text-gray-400">({p.percent}%)</span>
                </span>
                <span>{thousandSeperatorWithPrefix(p.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-medium border-t border-slate-200 mt-1 pt-1">
              <span>Total</span>
              <span>{getPrice2decimal(claimed, false, true)}</span>
            </div>
          </div>
        )}

        {live.length > 0 && (
          <div className="border border-slate-200 rounded-lg p-3">
            <p className="font-semibold text-sm mb-2">In the check queue</p>
            {live.map((p) => (
              <div className="flex justify-between text-sm py-0.5" key={p.id}>
                <span>{p.payeeName}</span>
                <span className={STATUS_STYLE[p.status] || ''}>
                  {thousandSeperatorWithPrefix(p.amount)} {p.status}
                  {p.referenceNumber ? ` #${p.referenceNumber}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {locked && (
          <p className="text-sm text-amber-700">
            A share of this fee has already been paid, so the split can no longer be changed. Void that payment first if
            it needs to.
          </p>
        )}

        {!locked && payouts.length > 0 && (
          <div className="flex justify-end">
            <span className="cursor-pointer text-shade-blue hover:underline text-sm" onClick={onQueue}>
              {live.length ? 'Re-queue these shares' : 'Queue these shares for payment'}
            </span>
          </div>
        )}
      </div>
    </Modal>
  )
}
